import './on-the-spot.css';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

const SCRIPT_URL = '/build/games/on-the-spot/on-the-spot.min.js';

import imgGameIcon from '../../images/game-icon.svg';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'WITAJ W PERFECT TAP',
    description: '<p>Przygotuj się na grę, która sprawdzi Twoje wyczucie, refleks i precyzję! ' +
        'Na ekranie pojawi się produkt, który trzeba umieścić w odpowiednim miejscu w walizce.</p>' +
        '<p>Produkt porusza się automatycznie, a Twoim zadaniem jest zatrzymanie go jak najbliżej wyznaczonego punktu. Uważaj jednak – każdy poziom może mieć inną prędkość i stopień trudności, więc liczy się skupienie i idealny moment!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Obserwuj poruszający się produkt i wyznaczone miejsce w walizce.</li>' +
            '<li>Naciśnij w ekran, gdy produkt znajdzie się jak najbliżej celu.</li>' +
            '<li>Masz 3 próby – do wyniku liczy się najlepsza z nich!</li>' +
            '</ol>' +
            '<p>Sprawdź, jak precyzyjnie uda Ci się spakować produkt do walizki! ✨</p>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Im bliżej celu zatrzymasz produkt, tym więcej punktów zdobędziesz.</li>' +
            '<li>🎯 Najlepszy wynik z 3 prób trafia do punktacji.</li>' +
            '<li>⚡ Kolejne poziomy mogą być coraz trudniejsze.</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 40 punktów.</p>',
    },
};

import imgCase from './images/case.webp';
import imgBackground from './images/background.webp';
import imgStarParticle from './images/star.svg';
import imgLevelUp from './images/level-up.svg';

let gameInstance  = null;
let currentTaskId = null;

// Per-session event tracking state.
let _sessionToken = null;
let _seq = 0;
let _startTime = null;

function resetSessionState() {
    currentTaskId = null;
    _sessionToken = null;
    _seq = 0;
    _startTime = null;
}

function sendEventRequest(taskId, path, payload) {
    fetch(`/api/task/${taskId}/on-the-spot/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(() => {}); // fire-and-forget, must not interrupt gameplay
}

function attachEventListeners(taskId) {
    if (!gameInstance?.eventBus) return;

    // sessionToken is set by the game on this.sessionToken after startGame() resolves,
    // and game:start fires immediately after — so it is always available here.
    gameInstance.eventBus.on('game:start', () => {
        _sessionToken = gameInstance.sessionToken ?? null;
        _seq = 0;
        _startTime = Date.now();
    });

    gameInstance.eventBus.on('spot:tapped', (data) => {
        if (!_sessionToken) return;
        _seq++;
        sendEventRequest(taskId, 'event/tapped', {
            sessionToken: _sessionToken,
            seq: _seq,
            elapsedMs: _startTime !== null ? Date.now() - _startTime : 0,
            levelIndex: data.levelIndex,
            tryIndex: data.tryIndex,
            inBoundary: data.inBoundary,
            distance: data.distance,
            position: data.position,
        });
    });

    gameInstance.eventBus.on('level:complete', (data) => {
        if (!_sessionToken) return;
        _seq++;
        sendEventRequest(taskId, 'event/level-complete', {
            sessionToken: _sessionToken,
            seq: _seq,
            elapsedMs: _startTime !== null ? Date.now() - _startTime : 0,
            levelIndex: data.levelIndex,
            bestDistance: data.bestDistance,
            bestInBoundary: data.bestInBoundary,
        });
    });
}

const ASSETS_CONFIG = {
    texts: {
        gameTitle: 'Perfect Tap',
        gameSubtitle: 'Upuść produkt w odpowiednim miejscu!',
        startButton: 'Rozpocznij grę',
        tapButton: 'TAP!',
        tryPillLabel: 'Próba',
        levelNotice: 'Poziom {current} z {total}',
        levelModalTitle: 'Poziom {current} ukończony!',
        levelModalText: 'Przygotuj się na kolejny poziom!',
        levelModalTitleSuccess: 'Idzie Ci świetnie!',
        levelModalTextSuccess: 'Przygotuj się na następny poziom!',
        levelModalTitleFail: 'Nie poddawaj się!',
        levelModalTextFail: 'Trening czyni mistrza!',
        levelModalButton: 'Następny poziom',
        noHitsModalTitle: 'Będzie dobrze...',
        noHitsModalText: 'Następnym razem na pewno się uda!',
        feedbackInZone: 'TRAFIONE!',
        feedbackOutOfZone: 'PUDŁO!',
        resultTitle: 'Twój wynik',
        resultAccuracy: 'Celność',
        resultInZone: 'W strefie',
        resultRankingPoints: 'Punkty rankingowe',
        resultRedeemablePoints: 'Punkty do wymiany',
        yes: 'Tak',
        no: 'Nie',
        playAgain: 'Zagraj ponownie',
        loading: 'Ładowanie...',
    },
    images: {
        background: imgBackground,
        logo: null,
        caseImage: imgCase,
        starParticle: imgStarParticle,
        levelModalImage: imgLevelUp,
        noHitsModalImage: imgLevelUp,
    },
    sounds: {correct: null, wrong: null, gameOver: null},
    colors: {
        primary: '#0d6efd',
        success: '#4cff91',
        danger: '#ff4f4f',
        dark: '#111111',
        background: 'linear-gradient(180deg, #FF66CC 1.79%, #FF4E31 50.7%, #FFAF45 97.7%),' +
            'linear-gradient(333.62deg, #FF66CC 0%, #FF4E31 38.66%, #FFAF45 75.8%)',
        surface: 'rgba(255, 255, 240, 1)',
        text: '#ffffff',
        textMuted: '#aaaaaa',
        btnGradient: 'linear-gradient(160deg, #FFAF45 9.47%, #FF4E31 49.5%, #FF66CC 89.54%)',
        caseBgGradient: 'linear-gradient(0deg, #000000 0%, #333333 44.23%, #B5B5B5 100%)',
    },
};

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

export function renderOnTheSpot(modal, data, markCardDone) {
    const taskId = data.task.id;
    currentTaskId = taskId;
    const cfg = data.onTheSpotGame.config;

    const el = contentEl(modal);
    el.innerHTML = '';

    const containerId = 'ots-game-container';
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'ots-game';
    el.appendChild(container);

    resetSessionState();

    loadScript(SCRIPT_URL)
        .then(() => {
            if (typeof OnTheSpotGame === 'undefined') {
                console.error('OnTheSpotGame: script loaded but OnTheSpotGame is not defined');
                return;
            }

            gameInstance = OnTheSpotGame.init(containerId, {
                assetsConfig: ASSETS_CONFIG,
                gameConfig: {
                    tries: cfg.tries ?? 3,
                    zoneTolerance: cfg.zoneTolerance ?? 0.08,
                    baseSpeed: 0.5,
                    betweenTryDelay: 1200,
                    betweenLevelDelay: 2000,
                    autoStart: true,

                    caseImageInset: { top: 0.00, bottom: 0.11, left: 0.09, right: 0.18 },
                },
                apiConfig: {
                    useMock: false,
                    endpoints: {
                        startGame: `/api/task/${taskId}/on-the-spot/start`,
                        submitResult: `/api/task/${taskId}/on-the-spot/submit`,
                    },
                },
                callbacks: {
                    onError: (error) => {
                        if (error?.status === 409 && (error.data?.error === 'already_completed' || error.data?.error === 'game_in_progress')) {
                            renderGameMessage(modal, startBlockedMessage(error.data?.error));
                            return;
                        }
                        document.dispatchEvent(new CustomEvent('game-modal:request-close'));
                        alert(`Błąd gry: ${error}`);
                    },
                    onComplete: (result) => {
                        markCardDone({rankingPointsEarned: result.rankingPointsEarned ?? 0, redeemablePointsEarned: result.redeemablePointsEarned ?? 0, checkIn: result.checkIn});
                        document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                            detail: {
                                rankingPoints: result.rankingPointsEarned ?? 0,
                                redeemablePoints: result.redeemablePointsEarned ?? 0,
                            },
                        }));
                    },
                },
            });

            attachEventListeners(taskId);
        })
        .catch(() => console.error('OnTheSpotGame: failed to load game script'));
}

export async function forceEndOnTheSpot() {
    if (gameInstance) {
        try {
            if (currentTaskId !== null) {
                await fetch(`/api/task/${currentTaskId}/on-the-spot/flag-force-end`, { method: 'POST' });
            }
            gameInstance.forceEnd();
        } catch { /* ignore */ }
    }
}

export function teardownOnTheSpot(modal) {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */ }
        gameInstance = null;
    }
    resetSessionState();
    const el = contentEl(modal);
    if (el) el.innerHTML = '';
}
